import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import EuropeMap from "../components/EuropeMap";
import Papa from "papaparse";
import "../styles/Europe.css";

// Object mapping country codes to full names
const countryNames = {
  AT: "Austria",
  BE: "Belgium",
  BG: "Bulgaria",
  CH: "Switzerland",
  CY: "Cyprus",
  DE: "Germany",
  ES: "Spain",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  HR: "Croatia",
  HU: "Hungary",
  IE: "Ireland",
  IL: "Israel",
  IS: "Iceland",
  IT: "Italy",
  LT: "Lithuania",
  LV: "Latvia",
  ME: "Montenegro",
  NL: "Netherlands",
  NO: "Norway",
  PL: "Poland",
  PT: "Portugal",
  RS: "Serbia",
  SE: "Sweden",
  SI: "Slovenia",
  SK: "Slovakia",
};


// Enum for the type of the disease
const DiseaseTypes = Object.freeze({
    HighBloodPressure: "High Blood Pressure",
    HeartDisease: "Heart Disease",
});

const getDiseaseCSVFile = (diseaseType) => {
    switch (diseaseType) {
      case DiseaseTypes.HighBloodPressure:
        return "/data/europe_csv/allginhbp.csv"
      case DiseaseTypes.HeartDisease:
        return "/data/europe_csv/allginhrt.csv";
      default:
        return null;
    }
}

// Array from DiseaseTypes
const dropdownChoices = Object.values(DiseaseTypes);

// console.log("choices:", dropdownChoices);


function Europe() {
  const [data, setData] = useState([]);

  // State variable which take country codes as values
  const [selectedCountry, setSelectedCountry] = useState("");

  // State variables which values are from DiseaseTypes enum
  const [selectedCondition, setSelectedCondition] = useState("");
  const [diseaseType, setDiseaseType] = useState(DiseaseTypes.HighBloodPressure);


  useEffect(() => {
    const fetchData = async () => {
      // Decide which CSV file to load based on the selected condition
      const fileName = getDiseaseCSVFile(diseaseType);
      if (!fileName) {
        return;
      }

      // Load the CSV file
      const response = await fetch(fileName);
      const text = await response.text();

      // Parse the CSV data using PapaParse
      Papa.parse(text, {
        header: true, // Use the first row as column names
        complete: (result) => {
          // Clean up the country names (remove extra spaces and quotes)
          const cleanedData = result.data.map((row) => ({
            ...row,
            cntr: row.cntr ? row.cntr.trim().replace(/^"|"$/g, "") : "",
          }));

          // Save the cleaned data to state
          setData(cleanedData);
        },
      });
    };

    fetchData(); // Run the fetch function
  }, [diseaseType]); // Re-run whenever the selected condition changes

  // Handle the selected country from dropdown
  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };

  // Handle condition selection (changes the type for fetching the correct file)
  const handleConditionChange = (e) => {
    const condition = e.target.value;

    // console.log("condition:", condition);
    setSelectedCondition(condition);
    setDiseaseType(condition);
  };

  const getHLR = (selectedCountry) => {
    const row = data.find((d) => d.cntr === selectedCountry);
    const value = parseFloat(row?.hlr);
    return isNaN(value) ? "N/A" : value.toFixed(1);
  };

  const getGiniCoeff = (selectedCountry) => {
    const row = data.find((d) => d.cntr === selectedCountry);
    const value = parseFloat(row?.ginsum);
    return isNaN(value) ? "N/A" : value.toFixed(2);
  };

  // console.log("Loaded data:", data);

  return (
    <div className="europe-container">
      <Header />

      <div className="europe-content-wrapper">
        {/* Left side panel with dropdowns and stats */}
        <div className="controls-card">
          <h2>Explore Heart Condition Rates</h2>
          <div className="dropdown-group">
            {/* Country selection dropdown */}
            <div className="dropdown-container">
              <label htmlFor="country">Select Country:</label>
              <select
                id="country"
                className="styled-dropdown"
                value={selectedCountry}
                onChange={handleCountryChange}
              >
                <option value="">All Countries</option>
                {data.map((d, index) => (
                  <option key={index} value={d.cntr}>
                    {countryNames[d.cntr]}
                  </option>
                ))}
              </select>
            </div>

            {/* Condition selection dropdown */}
            <div className="dropdown-container">
              <label htmlFor="condition">Select Condition:</label>
              <select
                id="condition"
                className="styled-dropdown"
                value={selectedCondition}
                onChange={handleConditionChange}
              >
                <option value="">Select Condition</option>
                {dropdownChoices.map((condition, index) => (
                  <option key={`${condition}-${index}`} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Show stats only when a country and condition is selected */}
          {selectedCountry && selectedCondition && (
            <div className="stats-card">

              <h2 className="stats-card-title">
                {selectedCondition} in {countryNames[selectedCountry]}
              </h2>

              <div className="stats-hlr-text">

                <h3 className="stats-card-subtitle">
                  Highest to Lowest Ratio
                </h3>

                Adults of the lowest income class are

                <div className="stats-card-value" id="hlr-value">
                  {` ${getHLR(selectedCountry)} `}
                </div>

                times more likely to be diagnosed with {selectedCondition.toLowerCase()} than the highest income class.
              </div>

              <div className="stats-gini-text">
                <h3 className="stats-card-subtitle">
                  Gini coefficient
                </h3>

                <div className="stats-card-value">
                  {getGiniCoeff(selectedCountry)}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Right side with the map */}
        <div className="map-section">
          <EuropeMap
            data={data}
            selectedCountry={selectedCountry}
            selectedCondition={selectedCondition}
            setSelectedCountry={setSelectedCountry}
            countryNames={countryNames}
          />
        </div>
      </div>
    </div>
  );
}

export default Europe;
